import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, HasProductContext, withProduct } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import translate from "@insite/client-framework/Translate";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FormattedCommaSeparatedValue } from "@MasterBluePrint/Services/FormattedValueService";
import React, { FC, useEffect } from "react";
import { css } from "styled-components";

interface OwnProps {
    extendedStylesTable?: ProductQuantityBreakPricingStyles;
}

export interface ProductQuantityBreakPricingStyles {
    viewLink?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    priceBreakContainer?: InjectableCss;
}

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    modal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: .625rem .625rem .625rem 1.25rem;
            `,
            headlineTypography: css`
                font-size: 1.25rem;
                margin-bottom: 0;
            `,
        },
    },
    breakQtyHeader: { alignX: "center" },
    breakPriceHeader: { alignX: "center" },
    savingsHeader: {
        css: css`
            display: none;
        `,
    },
    breakQtyCell: { alignX: "center" },
    breakPriceCell: { alignX: "center" },
    savingsCell: {
        css: css`
            display: none;
        `,
    },
    priceBreakContainer: {
        css: css`
            margin-left: .625rem;
            margin-bottom: .625rem;
        `,
    },
    table: {
        css: css`
            background-color: transparent;
            margin: 1.5rem 0 1rem;
            padding: .5rem .5rem;
            th {
                background-color: transparent !important;
            }
            tbody tr:nth-child(odd) {
                background: #f2f5f7;
            }
            tbody tr:nth-child(even) {
                background: transparent;
            }

            thead tr th,
            tbody tr td {
                border-bottom: none;
            }
            tbody tr td {
                height: 2.5rem;
            }

            thead tr th span {
                font-size: 1.1rem;
                color: #333f48;
                font-weight: 600;
            }
        `,
    },
    tableRow: {
        css: css`
            background-color: #e0f1fc !important;
            background: #e0f1fc !important;
        `,
    },
};

const ProductQuantityBreakPricingTable: FC<OwnProps & HasProduct> = ({
    product,
    extendedStylesTable,
    productInfo: { pricing, qtyOrdered },
}) => {
    const [orderedQty, setOrderedQty] = React.useState(0);
    useEffect(() => {
        setOrderedQty(qtyOrdered);
    }, [qtyOrdered]);

    const [styles] = React.useState(() => mergeToNew(productQuantityBreakPricingStyles, extendedStylesTable));

    if (
        !product ||
        product.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2
    ) {
        return null;
    }
    const hasBillTo = () => !!getCookie("CurrentBillToId");
    const hasBillToErp = () => !!getCookie("CurrentBillToErp");

    return (
        <>
            <StyledWrapper id="pdpQtyTableToggle">
                <DataTable {...styles.table}>
                    <DataTableHead {...styles.tableHead}>
                        <DataTableHeader {...styles.breakQtyHeader}>{translate("Qty:")}</DataTableHeader>
                        <DataTableHeader {...styles.breakPriceHeader}>{translate("Price:")}</DataTableHeader>
                        <DataTableHeader {...styles.savingsHeader} title={translate("Savings")} />
                    </DataTableHead>
                    <DataTableBody {...styles.tableBody}>
                        {pricing!.unitRegularBreakPrices!.map((breakPrice, index) => (
                            <DataTableRow
                                {...(index === 0 &&
                                Number(orderedQty) <= Number(pricing!.unitRegularBreakPrices![index + 1]?.breakQty) - 1
                                    ? { ...styles.tableRow }
                                    : Number(orderedQty) >= Number(breakPrice.breakQty) &&
                                      Number(orderedQty) <=
                                          Number(pricing!.unitRegularBreakPrices![index + 1]?.breakQty) - 1
                                    ? { ...styles.tableRow }
                                    : pricing!.unitRegularBreakPrices &&
                                      pricing!.unitRegularBreakPrices?.length === index + 1 &&
                                      Number(orderedQty) >= Number(breakPrice.breakQty)
                                    ? { ...styles.tableRow }
                                    : "")}
                                key={breakPrice.breakQty}
                                data-test-selector="breakPriceRow"
                            >
                                <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                    {hasBillTo() && hasBillToErp()
                                        ? pricing!.unitRegularBreakPrices?.length === index + 1
                                            ? `${FormattedCommaSeparatedValue(breakPrice.breakQty.toString())} ${translate("or more")}`
                                            : `${FormattedCommaSeparatedValue(breakPrice.breakQty.toString())}-${FormattedCommaSeparatedValue((
                                                  pricing!.unitRegularBreakPrices![index + 1].breakQty - 1
                                              ).toString())}`
                                        : FormattedCommaSeparatedValue(breakPrice.breakQty)}
                                </DataTableCell>
                                <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                    {breakPrice.breakPriceDisplay}
                                </DataTableCell>
                                <DataTableCell {...styles.savingsCell} data-test-selector="message">
                                    {breakPrice.savingsMessage}
                                </DataTableCell>
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </>
    );
};
export default withProduct(ProductQuantityBreakPricingTable);
